/**
 * The Ask FFH answer stream, as ffh-public-api sends it (ADR-006): server-sent
 * events, each an `event:` name and one JSON `data:` line, ending with exactly
 * one `done` or `error`. Comment lines (the 15-second heartbeat) are ignored.
 */

export type LookupOutcome =
  | 'ok'
  | 'unknown_lookup'
  | 'invalid_input'
  | 'failed';

export type AskFfhStreamEvent =
  // A backend from before stored chats (FFH-3325) sends no `conversationId`.
  | { event: 'started'; data: { conversationId?: string } }
  | { event: 'text'; data: { text: string } }
  | { event: 'lookup_started'; data: { id: string; name: string } }
  | {
      event: 'lookup_finished';
      data: { id: string; name: string; outcome: LookupOutcome };
    }
  | { event: 'round_finished'; data: { round: number } }
  | { event: 'done'; data: Record<string, never> }
  | { event: 'error'; data: { code: string; message: string } };

const KNOWN_EVENTS = new Set<string>([
  'started',
  'text',
  'lookup_started',
  'lookup_finished',
  'round_finished',
  'done',
  'error'
]);

const EVENT_FIELD = 'event:';
const DATA_FIELD = 'data:';

/** One blank-line-separated SSE block as an event, or null if unusable. */
const parseBlock = (block: string): AskFfhStreamEvent | null => {
  let name = '';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith(EVENT_FIELD)) {
      name = line.slice(EVENT_FIELD.length).trim();
    } else if (line.startsWith(DATA_FIELD)) {
      dataLines.push(line.slice(DATA_FIELD.length).trimStart());
    }
  }
  if (!KNOWN_EVENTS.has(name) || dataLines.length === 0) return null;
  try {
    return {
      event: name,
      data: JSON.parse(dataLines.join('\n'))
    } as AskFfhStreamEvent;
  } catch {
    return null;
  }
};

/** Calls `onEvent` for every event in the stream, in order. */
export const readAskFfhStream = async (
  body: ReadableStream<Uint8Array>,
  onEvent: (event: AskFfhStreamEvent) => void
): Promise<void> => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const flush = (isFinal: boolean) => {
    const blocks = buffer.replace(/\r\n/g, '\n').split('\n\n');
    buffer = isFinal ? '' : blocks.pop() ?? '';
    for (const block of blocks) {
      const event = parseBlock(block);
      if (event) onEvent(event);
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    flush(false);
  }
  buffer += decoder.decode();
  flush(true);
};
